// Sprint AW · shared TransferOwnershipDialog — used from Admin → Events AND Event Team page.
// Single source of truth: no duplicated ownership-transfer business logic.
import { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { adminApi } from "../lib/events";
import { extractError } from "../lib/api";

export default function TransferOwnershipDialog({ event, onClose, onDone }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null); // null = not searched; [] = searched-empty
  const [selected, setSelected] = useState(null);
  const [keepAsMember, setKeepAsMember] = useState(true);
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");

  const runSearch = useCallback(async (term) => {
    const trimmed = (term || "").trim();
    if (!trimmed) { setResults(null); setSearchError(""); return; }
    setSearching(true);
    setSearchError("");
    try {
      const res = await adminApi.accounts(trimmed, { eligibleForEvent: event.id });
      setResults(res || []);
    } catch (e) {
      setSearchError(extractError(e) || "Search failed.");
      setResults(null);
    } finally { setSearching(false); }
  }, [event.id]);

  useEffect(() => {
    const t = setTimeout(() => runSearch(q), 300);
    return () => clearTimeout(t);
  }, [q, runSearch]);

  const submit = async () => {
    if (!selected) { toast.error("Please select the new Event Owner."); return; }
    if (confirm.trim().toUpperCase() !== "TRANSFER") { toast.error("Please type TRANSFER to confirm."); return; }
    setSaving(true);
    try {
      const res = await adminApi.transferEventOwner(event.id, {
        new_owner_id: selected.id,
        keep_previous_owner_as_team_member: keepAsMember,
      });
      toast.success(`Ownership transferred to ${res.new_owner?.email}`);
      onDone(res);
    } catch (e) { toast.error(extractError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="admin-transfer-ownership-dialog">
        <DialogHeader>
          <DialogTitle>Transfer Ownership — {event.title}</DialogTitle>
          <DialogDescription>
            Only Platform Admin can transfer canonical event ownership. This is server-enforced.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <div className="gt-card p-3">
            <div className="gt-label mb-1">Current Event Owner</div>
            <div className="font-semibold" data-testid="transfer-current-owner-name">
              {event.organiser_name || event.organiser_email || "—"}
            </div>
            <div className="text-xs font-mono text-[var(--gt-ink-3)]" data-testid="transfer-current-owner-email">
              {event.organiser_email}
            </div>
            {event.organiser_business_name && (
              <div className="text-xs text-[var(--gt-ink-2)]">{event.organiser_business_name}</div>
            )}
          </div>

          <div>
            <label className="gt-form-label">New Event Owner (search by name, email or business name)</label>
            <div className="flex gap-2">
              <input
                className="gt-input flex-1"
                placeholder="Type name, email, or business name…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); runSearch(q); } }}
                data-testid="transfer-search-input"
                autoFocus
              />
              <button type="button" onClick={() => runSearch(q)} disabled={searching}
                      className="gt-btn-ghost text-sm" data-testid="transfer-search-btn">
                {searching ? <Loader2 size={14} className="animate-spin" /> : "Search"}
              </button>
            </div>
            <p className="text-[11px] text-[var(--gt-ink-3)] mt-1" data-testid="transfer-search-hint">
              Current event owner is excluded from search results.
            </p>

            {searching && q.trim() && (
              <div className="mt-2 text-xs text-[var(--gt-ink-3)] flex items-center gap-2" data-testid="transfer-search-loading">
                <Loader2 size={12} className="animate-spin" /> Searching…
              </div>
            )}

            {!searching && searchError && (
              <div className="mt-2 gt-card p-3 border-l-4 border-[var(--gt-accent)] text-xs" data-testid="transfer-search-error">
                <div className="font-bold mb-1">Search failed</div>
                <div className="text-[var(--gt-ink-2)] mb-2">{searchError}</div>
                <button type="button" onClick={() => runSearch(q)}
                        className="gt-btn-ghost text-xs" data-testid="transfer-search-retry">Retry</button>
              </div>
            )}

            {!searching && !searchError && Array.isArray(results) && results.length > 0 && (
              <div className="mt-2 max-h-40 overflow-y-auto border border-[var(--gt-line)] rounded-sm" data-testid="transfer-search-results">
                {results.map((o) => (
                  <button
                    type="button"
                    key={o.id}
                    onClick={() => setSelected(o)}
                    className={`w-full text-left px-3 py-2 text-xs border-b border-[var(--gt-line)] last:border-b-0 hover:bg-[var(--gt-line)] ${selected?.id === o.id ? "bg-[var(--gt-line)]" : ""}`}
                    data-testid={`transfer-search-result-${o.id}`}
                  >
                    <div className="font-semibold">{o.name || o.business_name || "—"}</div>
                    <div className="font-mono text-[var(--gt-ink-3)]">{o.email}</div>
                    {o.business_name && o.name && (
                      <div className="text-[var(--gt-ink-2)]">{o.business_name}</div>
                    )}
                  </button>
                ))}
              </div>
            )}

            {!searching && !searchError && Array.isArray(results) && results.length === 0 && q.trim() && (
              <div className="mt-2 gt-card p-3 text-xs text-[var(--gt-ink-2)]" data-testid="transfer-search-empty">
                No eligible organisers found. Try a different name, business name, or email.
                The current event owner is excluded from search results.
              </div>
            )}

            {selected && (
              <div className="mt-2 gt-card p-3 border-l-4 border-emerald-600" data-testid="transfer-selected-owner">
                <div className="gt-label mb-1">Selected new owner</div>
                <div className="font-semibold">{selected.name || selected.business_name || "—"}</div>
                <div className="text-xs font-mono">{selected.email}</div>
              </div>
            )}
          </div>

          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" checked={keepAsMember} onChange={(e) => setKeepAsMember(e.target.checked)}
                   data-testid="transfer-keep-previous-toggle" />
            <span className="text-xs">
              <strong>Keep previous owner as Event Manager</strong> (default ON).<br />
              Assigns the Sprint AP-safe Event Manager preset — no refunds processing,
              no team-management, no ownership-transfer capabilities.
            </span>
          </label>

          <div className="gt-card p-3 border-l-4 border-[var(--gt-accent)] text-xs" data-testid="transfer-financial-warning">
            <div className="font-bold uppercase tracking-widest text-[10px] mb-1">Financial ownership cutover</div>
            Existing completed sales and historical payout records remain assigned to the previous organiser.
            New sales after ownership transfer will be assigned to the new Event Owner.
            <br /><br />
            Event ID and public URL are unchanged. Stripe/payment records are not modified.
            Pending refunds on historic orders remain with the original organiser.
          </div>

          <div>
            <label className="gt-form-label">Type <span className="font-mono">TRANSFER</span> to confirm</label>
            <input className="gt-input font-mono" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                   placeholder="TRANSFER" data-testid="transfer-confirm-input" />
          </div>

          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="gt-btn-ghost text-sm" data-testid="transfer-cancel-btn">Cancel</button>
            <button type="button" onClick={submit}
                    disabled={saving || !selected || confirm.trim().toUpperCase() !== "TRANSFER"}
                    className="gt-btn-primary text-sm" data-testid="transfer-confirm-btn">
              {saving ? <Loader2 size={14} className="animate-spin" /> : null}
              Confirm Ownership Transfer
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
